/**
 * Workers AI API Service
 * Interfaces with new Cloudflare Workers AI endpoints
 */

export interface IdeasRequest {
  topic: string;
  audience?: string;
  language?: 'en' | 'it' | 'de';
  count?: number;
}

export interface IdeasResponse {
  ideas: string[];
  cached: boolean;
  language: string;
}

export interface ScriptRequest {
  hook: string;
  topic?: string;
  language?: 'en' | 'it' | 'de';
  duration?: number;
  brandStyle?: string;
  maxLength?: number;
  temperature?: number;
}

export interface ScriptBeat {
  section: 'hook' | 'value' | 'cta';
  text: string;
  duration: number;
}

export interface ScriptResponse {
  script: string;
  captions: string[];
  beats: ScriptBeat[];
  wordCount: number;
  estimatedDuration: number;
  cached: boolean;
  language: string;
}

export interface StoryboardRequest {
  script: string;
  duration?: number;
  language?: 'en' | 'it' | 'de';
  visualStyle?: string;
  beats?: ScriptBeat[];
}

export interface StoryboardBeat {
  sec: number;
  duration: number;
  text: string;
  assetTags: string[];
  overlay: string;
  caption: string;
  visualDirection: string;
}

export interface StoryboardResponse {
  storyboard: StoryboardBeat[];
  totalDuration: number;
  assetRequirements: {
    totalAssets: number;
    uniqueTags: string[];
    styleRequirements: string[];
  };
  cached: boolean;
  language: string;
}

export interface TTSRequest {
  text: string;
  voice?: string;
  language?: 'en' | 'it' | 'de';
  speed?: number;
  draftId?: string;
  format?: 'mp3' | 'wav';
}

export interface TTSResponse {
  audioUrl: string;
  r2Key: string;
  duration: number;
  cached: boolean;
  voice: string;
  format: string;
}

export interface RenderJobRequest {
  draftId: string;
  params?: {
    resolution?: string;
    fps?: number;
    style?: string;
    captions_on?: boolean;
    watermark?: boolean;
  };
}

export interface RenderJobResponse {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  estimatedTime?: string;
}

export interface JobStatusResponse {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  videoUrl?: string;
  error?: string;
  createdAt: string;
  completedAt?: string;
}

export interface ThumbnailRequest {
  prompt: string;
  style?: 'photorealistic' | 'illustration' | 'minimalist' | 'bold' | 'professional';
  format?: 'square' | 'landscape' | 'portrait';
  textOverlay?: {
    title: string;
    subtitle?: string;
    style?: 'bold' | 'clean' | 'modern';
  };
  brandColors?: {
    primary: string;
    secondary?: string;
    accent?: string;
  };
  draftId?: string;
}

export interface ThumbnailResponse {
  imageUrl: string;
  r2Key: string;
  prompt: string;
  style: string;
  cached: boolean;
  dimensions: string;
}

export interface AssetSearchRequest {
  query: string;
  tags?: string[];
  style?: string;
  limit?: number;
  threshold?: number;
}

export interface AssetMatch {
  id: string;
  url: string;
  title: string;
  description: string;
  tags: string[];
  style: string;
  duration?: number;
  thumbnail?: string;
  score: number;
  source: 'stock' | 'uploaded' | 'generated';
}

export interface AssetSearchResponse {
  matches: AssetMatch[];
  query: string;
  totalResults: number;
  searchTime: number;
  cached: boolean;
}

export interface AssetIndexRequest {
  assets?: AssetToIndex[];
  batchSize?: number;
  forceReindex?: boolean;
}

export interface AssetToIndex {
  id: string;
  url: string;
  title: string;
  description: string;
  tags: string[];
  style: string;
  duration?: number;
  thumbnail?: string;
  source: 'stock' | 'uploaded' | 'generated';
}

export interface IndexResponse {
  indexed: number;
  skipped: number;
  errors: number;
  totalTime: number;
  message: string;
}

class WorkersAIService {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  private async makeRequest<T>(endpoint: string, data: any): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}/api/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  private async getRequest<T>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}/api/${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  /**
   * Generate viral hooks/ideas for content
   */
  async generateIdeas(request: IdeasRequest): Promise<IdeasResponse> {
    return this.makeRequest<IdeasResponse>('ai/ideas', request);
  }

  /**
   * Generate video script from hook
   */
  async generateScript(request: ScriptRequest): Promise<ScriptResponse> {
    return this.makeRequest<ScriptResponse>('ai/script', request);
  }

  /**
   * Generate storyboard from script
   */
  async generateStoryboard(request: StoryboardRequest): Promise<StoryboardResponse> {
    return this.makeRequest<StoryboardResponse>('ai/storyboard', request);
  }

  /**
   * Generate voiceover using TTS
   */
  async generateTTS(request: TTSRequest): Promise<TTSResponse> {
    return this.makeRequest<TTSResponse>('ai/tts', request);
  }

  /**
   * Queue video rendering job
   */
  async queueRenderJob(request: RenderJobRequest): Promise<RenderJobResponse> {
    return this.makeRequest<RenderJobResponse>('queue/render', request);
  }

  /**
   * Get job status
   */
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    return this.getRequest<JobStatusResponse>(`queue/render/${jobId}`);
  }

  /**
   * Generate thumbnail image
   */
  async generateThumbnail(request: ThumbnailRequest): Promise<ThumbnailResponse> {
    return this.makeRequest<ThumbnailResponse>('ai/thumbnail', request);
  }

  /**
   * Search for assets using semantic search
   */
  async searchAssets(request: AssetSearchRequest): Promise<AssetSearchResponse> {
    return this.makeRequest<AssetSearchResponse>('assets/search', request);
  }

  /**
   * Index assets for semantic search
   */
  async indexAssets(request: AssetIndexRequest): Promise<IndexResponse> {
    return this.makeRequest<IndexResponse>('assets/index', request);
  }

  /**
   * Find assets for storyboard beats
   */
  async findAssetsForStoryboard(storyboard: StoryboardResponse): Promise<{
    beatAssets: Array<{
      beatIndex: number;
      assets: AssetMatch[];
    }>;
    totalAssetsFound: number;
  }> {
    const beatAssets: Array<{ beatIndex: number; assets: AssetMatch[] }> = [];
    let totalAssetsFound = 0;

    for (let i = 0; i < storyboard.storyboard.length; i++) {
      const beat = storyboard.storyboard[i];
      
      try {
        // Create search query from beat requirements
        const query = `${beat.visualDirection} ${beat.text}`;
        
        const searchResult = await this.searchAssets({
          query,
          tags: beat.assetTags,
          limit: 5,
          threshold: 0.6
        });

        beatAssets.push({
          beatIndex: i,
          assets: searchResult.matches
        });

        totalAssetsFound += searchResult.matches.length;
      } catch (error) {
        console.error(`Failed to find assets for beat ${i}:`, error);
        beatAssets.push({
          beatIndex: i,
          assets: []
        });
      }
    }

    return {
      beatAssets,
      totalAssetsFound
    };
  }

  /**
   * Poll job status until completion
   */
  async pollJobStatus(
    jobId: string, 
    onProgress?: (status: JobStatusResponse) => void,
    pollInterval: number = 2000
  ): Promise<JobStatusResponse> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await this.getJobStatus(jobId);
          
          if (onProgress) {
            onProgress(status);
          }

          if (status.status === 'completed') {
            resolve(status);
          } else if (status.status === 'failed') {
            reject(new Error(status.error || 'Job failed'));
          } else {
            // Continue polling
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  /**
   * Complete workflow: Generate ideas → script → storyboard → thumbnail → TTS → render
   */
  async generateCompleteVideo(options: {
    topic: string;
    selectedHook?: string;
    language?: 'en' | 'it' | 'de';
    duration?: number;
    visualStyle?: string;
    voice?: string;
    thumbnailStyle?: 'photorealistic' | 'illustration' | 'minimalist' | 'bold' | 'professional';
    brandColors?: { primary: string; secondary?: string; accent?: string };
    renderParams?: RenderJobRequest['params'];
    onProgress?: (step: string, data?: any) => void;
  }): Promise<{
    ideas?: IdeasResponse;
    script: ScriptResponse;
    storyboard: StoryboardResponse;
    thumbnail: ThumbnailResponse;
    tts: TTSResponse;
    renderJob: RenderJobResponse;
  }> {
    const { 
      topic, 
      selectedHook, 
      language = 'en', 
      duration = 60, 
      visualStyle = 'modern',
      voice = 'alloy',
      thumbnailStyle = 'professional',
      brandColors,
      renderParams = {},
      onProgress 
    } = options;

    try {
      let hook = selectedHook;
      let ideas: IdeasResponse | undefined;

      // Step 1: Generate ideas if no hook provided
      if (!hook) {
        onProgress?.('Generating ideas...');
        ideas = await this.generateIdeas({ topic, language });
        hook = ideas.ideas[0]; // Use first idea
      }

      // Step 2: Generate script
      onProgress?.('Generating script...', { hook });
      const script = await this.generateScript({
        hook,
        topic,
        language,
        duration
      });

      // Step 3: Generate storyboard
      onProgress?.('Creating storyboard...', { script: script.script });
      const storyboard = await this.generateStoryboard({
        script: script.script,
        duration,
        language,
        visualStyle,
        beats: script.beats
      });

      // Step 4: Generate thumbnail
      onProgress?.('Creating thumbnail...', { hook });
      const thumbnailPrompt = `${hook} - ${topic}`;
      const draftId = `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const thumbnail = await this.generateThumbnail({
        prompt: thumbnailPrompt,
        style: thumbnailStyle,
        format: 'landscape',
        brandColors,
        draftId
      });

      // Step 5: Generate TTS
      onProgress?.('Generating voiceover...', { text: script.script });
      const tts = await this.generateTTS({
        text: script.script,
        voice,
        language,
        draftId
      });

      // Step 6: Queue render job
      onProgress?.('Queuing video render...', { draftId });
      const renderJob = await this.queueRenderJob({
        draftId,
        params: {
          resolution: '1080x1920',
          fps: 30,
          style: visualStyle,
          captions_on: true,
          ...renderParams
        }
      });

      return {
        ideas,
        script,
        storyboard,
        thumbnail,
        tts,
        renderJob
      };

    } catch (error) {
      console.error('Complete video generation failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const workersAI = new WorkersAIService();
export default WorkersAIService;